export interface Testimonial {
  name: string;
  city: string;
  quote: string;
}

export interface Tomo {
  n: number;
  color: string;
  title: string;
  intro: string;
  listLabel: string;
  items: string[];
  closing: string;
}

export interface Bono {
  n: number;
  title: string;
  desc: string;
  items?: string[];
  note?: string;
  price: string;
  mockup: string;
}

export interface PricingOption {
  id: 'megapack';
  name: string;
  description: string;
  price: string;
  checkoutUrl: string;
}


export const HERO_MOCKUP =
  'https://aekpgsbqjzqntlbucbts.supabase.co/storage/v1/object/public/store-resources/81ca106c-e39d-4e14-89ec-17d7b73198fb/1789235007621_chatgpt-image-12-sept-2026-14-42-30.webp';

export const HERO_FEATURES: string[] = [
  '100% digital',
  'Acceso inmediato',
  'Todo nivel',
  'Para todo el año',
];

export const COST_STATS: { value: string; title: string; desc: string }[] = [
  {
    value: '52 domingos',
    title: 'Que podrías dejar de usar para planificar',
    desc: 'En vez de descansar, terminás pensando qué vas a hacer el lunes.',
  },
  {
    value: 'Cada semana',
    title: 'Volvés a empezar prácticamente desde cero',
    desc: 'Música, entrada en calor, técnica, combinaciones, secuencias y niveles diferentes.',
  },
  {
    value: 'Una y otra vez',
    title: 'Terminás recurriendo a lo mismo',
    desc: 'Cuando falta tiempo, las mismas combinaciones y dinámicas empiezan a aparecer en todas las clases.',
  },
];

export const PAIN_CARDS: { title: string; desc: string }[] = [
  {
    title: 'Llegás sin la clase completamente resuelta',
    desc: 'Tenés el grupo por entrar y todavía estás decidiendo qué ejercicio, combinación o secuencia trabajar.',
  },
  {
    title: 'Repetís demasiado las mismas combinaciones',
    desc: 'Sabés que querés renovar tus clases, pero crear contenido nuevo todas las semanas lleva tiempo.',
  },
  {
    title: 'Perdés horas buscando música e ideas',
    desc: 'Encontrás una canción, después buscás ejercicios, después pensás cómo unirlos… y una clase termina consumiendo toda tu tarde.',
  },
  {
    title: 'Tenés niveles mezclados',
    desc: 'Principiantes e intermedias dentro del mismo grupo y necesitás adaptar el contenido sin preparar tres clases distintas.',
  },
  {
    title: 'Tenés todo repartido',
    desc: 'Un ejercicio en un cuaderno, una canción guardada en WhatsApp, una secuencia anotada en el celular y una idea que no sabés dónde quedó.',
  },
  {
    title: 'Trabajás hasta los fines de semana',
    desc: 'Terminaste de dar clases, pero todavía quedan planificaciones, mensajes, ensayos, muestras y la semana siguiente.',
  },
];

export const BEFORE_ITEMS: string[] = [
  'Horas pensando qué enseñar.',
  'Clases que empiezan a sentirse repetitivas.',
  'Dificultad para trabajar con niveles diferentes.',
  'Música, ejercicios e ideas repartidos por todos lados.',
  'Ensayos y muestras preparados a último momento.',
  'Domingos usados para planificar la semana.',
];

export const AFTER_ITEMS: string[] = [
  'Elegís qué querés trabajar.',
  'Buscás la propuesta correspondiente.',
  'Adaptás el contenido a tu grupo.',
  'Tenés técnica, combinaciones y secuencias organizadas.',
  'Sumás ritmos y estilos diferentes.',
  'Contás con material para ensayos y muestras.',
  'Llegás a clase con mucha más claridad.',
];

export const TOMOS: Tomo[] = [
  {
    n: 1,
    color: '#F1D9C2',
    title: 'Planificación Profesional de Clases',
    intro: 'Dejá de empezar cada semana con una hoja en blanco.',
    listLabel: 'Incluye recursos para trabajar:',
    items: [
      'Estructura completa de una clase',
      'Entrada en calor',
      'Técnica',
      'Secuencias',
      'Objetivos semanales y mensuales',
      'Organización por niveles',
      'Planificación anual',
      'Seguimiento de grupos',
    ],
    closing: 'La estructura para ordenar todo tu año de clases.',
  },
  {
    n: 2,
    color: '#C9E1DA',
    title: 'Clases Listas · Nivel Inicial',
    intro: 'Material pensado para alumnas que están comenzando.',
    listLabel: 'Encontrás propuestas sobre:',
    items: [
      'Postura y brazos',
      'Caminatas',
      'Golpes de cadera',
      'Círculos',
      'Ochos',
      'Ondulaciones',
      'Shimmy básico',
      'Giros',
      'Secuencias progresivas',
    ],
    closing: 'Abrís, elegís qué querés trabajar y ya tenés una base.',
  },
  {
    n: 3,
    color: '#F2CDD2',
    title: 'Clases Listas · Nivel Intermedio',
    intro: 'Para avanzar en dificultad sin perder estructura.',
    listLabel: 'Trabajá:',
    items: [
      'Combinaciones completas',
      'Capas de movimientos',
      'Cambios de peso',
      'Shimmies',
      'Giros',
      'Desplazamientos',
      'Musicalidad',
      'Progresiones',
    ],
    closing: 'Más dificultad sin tener que crear todo desde cero.',
  },
  {
    n: 4,
    color: '#EAD5B8',
    title: 'Combinaciones y Secuencias',
    intro: 'Para esos días en los que necesitás una idea nueva y no querés pasar horas inventándola.',
    listLabel: 'Incluye propuestas de:',
    items: [
      'Cadera',
      'Brazos',
      'Giros',
      'Ochos',
      'Shimmies',
      'Ondulaciones',
      'Desplazamientos',
      'Cambios de dirección',
      'Secuencias cortas',
      'Secuencias completas',
    ],
    closing: 'Nunca más enfrentarte a una hoja en blanco sin saber qué hacer.',
  },
  {
    n: 5,
    color: '#DDC6D6',
    title: 'Ritmos, Estilos y Coreografías',
    intro: 'Sumá variedad a tus clases trabajando diferentes recursos de la danza.',
    listLabel: 'Incluye:',
    items: [
      'Oriental',
      'Baladi',
      'Saidi',
      'Shaabi',
      'Percusión',
      'Mejance',
      'Velo',
      'Bastón',
      'Improvisación',
      'Musicalidad',
      'Estructuras coreográficas',
    ],
    closing: 'Más variedad para vos. Más experiencias para tus alumnas.',
  },
  {
    n: 6,
    color: '#E6C2C8',
    title: 'Clases Especiales, Ensayos y Muestras',
    intro: 'Porque el trabajo de una profesora no termina cuando termina la clase.',
    listLabel: 'Encontrás material para:',
    items: [
      'Organizar ensayos',
      'Preparar coreografías',
      'Ordenar una muestra',
      'Distribuir alumnas en escenario',
      'Preparar presentaciones',
      'Crear checklists',
      'Organizar cierres de año',
      'Preparar clases temáticas',
    ],
    closing: 'También tenés resuelto todo lo que pasa fuera de la clase semanal.',
  },
];

export const TOMO_INCLUDES: string[] = [
  'Contenido listo para aplicar en clase',
  'Ideas adaptables a distintos niveles',
  'Material 100% digital y descargable',
  'Actualizado y pensado para instructores reales',
];

export const BONOS: Bono[] = [
  {
    n: 1,
    title: '50 Entradas en Calor',
    desc: '50 propuestas para dejar de comenzar siempre tus clases de la misma manera.',
    price: 'AR$14.990',
    mockup: 'https://cdn.magicpatterns.com/patterns/generated-images/8e9c15b6-98e9-4e27-b546-9bb187a41266.jpg',
  },
  {
    n: 2,
    title: '50 Ejercicios de Técnica',
    desc: 'Recursos para reforzar movimientos y sumar variedad a tus clases.',
    price: 'AR$9.990',
    mockup: 'https://cdn.magicpatterns.com/patterns/generated-images/f0a43592-b7c4-4e1d-b711-4722dad350fd.jpg',
  },
  {
    n: 3,
    title: 'Planner para Profesoras',
    desc: 'Organizá en un solo lugar:',
    items: ['Clases', 'Objetivos', 'Semanas', 'Ensayos', 'Grupos', 'Ideas', 'Seguimiento'],
    price: 'AR$7.990',
    mockup: 'https://cdn.magicpatterns.com/patterns/generated-images/6077e68f-2264-4ab9-ae96-1cda95b05398.jpg',
  },
  {
    n: 4,
    title: 'Guía Musical',
    desc: 'Una referencia rápida para seleccionar música y trabajar diferentes ritmos sin comenzar cada búsqueda desde cero.',
    price: 'AR$8.990',
    mockup: 'https://cdn.magicpatterns.com/patterns/generated-images/b51987b3-67e1-41c2-9ac1-c811e430b716.jpg',
  },
  {
    n: 5,
    title: '100 Ideas para Renovar tus Clases',
    desc: 'Para esos momentos en los que sentís que estás repitiendo demasiado.',
    note: 'Consultás una idea, elegís y la adaptás a tu grupo.',
    price: 'AR$8.990',
    mockup: 'https://cdn.magicpatterns.com/patterns/generated-images/09246f0e-86bd-426f-bba3-1502803ab435.jpg',
  },
];

export const DELIVERABLES: string[] = [
  'Los 6 contenidos digitales completos',
  'Clases organizadas por nivel',
  'Técnica, secuencias y combinaciones',
  'Ritmos, estilos y recursos coreográficos',
  'Material para ensayos y muestras',
  'Los 5 bonos exclusivos',
  'Acceso 100% digital',
  'Acceso de por vida',
];

export const FOR_YOU_ITEMS: string[] = [
  'Tenés varios grupos durante la semana.',
  'Enseñás después de trabajar todo el día.',
  'Tenés alumnas iniciales e intermedias.',
  'Querés sumar ideas nuevas.',
  'Estás preparando una muestra.',
  'Sentís que tus clases se están volviendo repetitivas.',
  'Recién empezás a enseñar y necesitás una estructura.',
  'Tenés experiencia, pero querés recuperar tiempo.',
];

export const FULL_TESTIMONIALS: Testimonial[] = [
  {
    name: 'Marina T.',
    city: 'Rosario',
    quote:
      'Lo que más me gustó es que no tenés que arrancar de cero. Abrís el tomo, elegís qué querés trabajar y ya tenés una base armada. A mí me ahorró un montón de tiempo, sobre todo entre semana.',
  },
  {
    name: 'Carla G.',
    city: 'Córdoba',
    quote:
      'Venía sintiendo que mis clases se estaban volviendo medio repetitivas y este material me dio un montón de ideas nuevas. Las combinaciones y los ejercicios de técnica me sirvieron muchísimo para variar.',
  },
  {
    name: 'Noelia P.',
    city: 'San Juan',
    quote:
      'Yo tengo grupos con niveles bastante mezclados y eso siempre me complicaba. Con las propuestas del MegaPack ahora me resulta mucho más fácil adaptar una misma clase sin volverme loca.',
  },
  {
    name: 'Luciana G.',
    city: 'Buenos Aires',
    quote:
      'El tomo de muestras y ensayos me vino bárbaro. Antes tenía todo entre el cuaderno, WhatsApp y notas sueltas. Ahora estoy mucho más organizada y llego a los ensayos con otra claridad.',
  },
];

export const FAQS: { q: string; a: string }[] = [
  {
    q: '¿Cómo recibo el material?',
    a: 'Después de completar tu compra recibís acceso digital al contenido para comenzar a utilizarlo.',
  },
  {
    q: '¿Sirve si recién empiezo a dar clases?',
    a: 'Sí. El material está organizado para ayudarte a darle estructura a tus clases. También puede ser utilizado por profesoras con experiencia que quieran ahorrar tiempo y sumar nuevas ideas.',
  },
  {
    q: '¿Para qué niveles sirve?',
    a: 'Incluye material específico para nivel inicial e intermedio, además de recursos adaptables a diferentes grupos.',
  },
  {
    q: '¿Los bonos están incluidos?',
    a: 'Sí. Con la oferta actual recibís los 6 contenidos + los 5 bonos.',
  },
  {
    q: '¿Es físico o digital?',
    a: 'Es 100% digital. Podés consultarlo desde computadora, tablet o celular.',
  },
  {
    q: '¿Tengo que pagar todos los meses?',
    a: 'No. Es un único pago.',
  },
  {
    q: '¿El acceso vence?',
    a: 'No. La compra incluye acceso de por vida al material adquirido.',
  },
  {
    q: '¿Tiene garantía?',
    a: 'Sí. Tu compra cuenta con una garantía de 7 días, sujeta a las condiciones informadas al momento de comprar.',
  },
];

export const PRICING_OPTIONS: PricingOption[] = [
  {
    id: 'megapack',
    name: 'MegaPack Árabe PRO',
    description: '6 contenidos completos',
    price: 'AR$19.990',
    checkoutUrl: 'https://megapack-arabe-pro.impultienda.ar/checkout',
  },
];

export const PRICING = {
  bonusValueOld: 'AR$50.950',
  totalValueOld: 'AR$99.940',
  savings: 'AR$79.950',
};
